// How the sim's units map onto the real world.
//
// Every species datasheet is written in REAL units — km/h, kg, metres, years —
// and converted through this module. That keeps species.ts readable as biology
// (and checkable against a source) instead of a wall of tuned magic numbers.
//
// TWO CLOCKS, and the reason why
// -----------------------------
// Movement and life history happen eight orders of magnitude apart: a deer runs
// 15 m/s but lives 5 years. No single clock makes both watchable, so the sim
// runs two and is explicit about it:
//
//   * MOVEMENT is literal. 1 px = 1 m and 1 sim-second = 1 real second, so a
//     deer sprinting at 18 px/s really is doing 64 km/h.
//   * LIFE HISTORY is compressed. Ages, lifespans and breeding intervals are
//     scaled by SIM_SECONDS_PER_YEAR so a generation takes a minute rather than
//     a decade. Only the RATIOS among them are taken from biology — a wolf
//     really does mature later and live longer than a deer, in proportion.
//
// The energy economy (econ.ts) rides the movement clock and is tuned for a
// playable system rather than derived from real caloric intake; mixing the two
// clocks makes any literal energy budget meaningless, so it isn't claimed.

/** Metres per pixel. The default world (4800×3600) is therefore 4.8 km × 3.6 km. */
export const M_PER_PX = 1;

/**
 * Sim-seconds per real year at the BASE compression, for life-history traits
 * only. At 90, a 5-year deer lives ~450 sim-seconds — fast enough that
 * multi-generation drift is observable in minutes.
 */
export const SIM_SECONDS_PER_YEAR = 90;

/**
 * Extra stretch applied to every life-history duration, on top of the base.
 * 1 = fastest evolution; higher = slower living animals and a more honest
 * ecosystem. THIS IS THE CENTRAL TRADE IN THE MODEL, so it is worth stating
 * exactly what it buys.
 *
 * The predator:prey biomass ratio is set by how much prey PRODUCTION the grass
 * supports, and production is standing stock divided by lifespan. Compress
 * lifespan and you inflate production, which inflates predator numbers. In
 * energy terms: a model deer at scale 1 eats only ~1.7 times its own body's
 * worth of food in a whole life, where a real deer eats roughly 18-36 times.
 * Deer are therefore far too cheap to produce, and wolves are correspondingly
 * too abundant — measured at 1:10 against a real 1:100-500.
 *
 * Stretching life history closes that gap directly, and costs exactly what it
 * buys: evolution takes proportionally longer to watch. Movement is untouched —
 * a deer still sprints at a real 64 km/h either way — so only DEMOGRAPHY slows,
 * never behaviour.
 *
 * Set once per run via setLifeScale() before the World is built; it is a module
 * singleton for the same reason ECON is, and changing it mid-run would be
 * incoherent because animals already alive carry their decoded lifespans.
 */
export let LIFE_SCALE = 1;

/** Set the life-history stretch for the run about to start. */
export function setLifeScale(x: number): void {
  LIFE_SCALE = x > 0 ? x : 1;
}

/** A life-history duration in sim-seconds, stretched by the run's scale. */
export const lifeSeconds = (simSeconds: number): number => simSeconds * LIFE_SCALE;

/**
 * Reference body mass. Species datasheets carry real kilograms; costs and draw
 * sizes use the dimensionless ratio mass/REF_MASS_KG, so a ~45 kg animal sits
 * near a body scale of 1 and the economy constants stay readable.
 */
export const REF_MASS_KG = 45;

/** Real metres → pixels. */
export const metres = (m: number): number => m / M_PER_PX;

/** Real km/h → px per sim-second. */
export const kmh = (v: number): number => (v * 1000) / 3600 / M_PER_PX;

/** Real years → sim-seconds (life-history clock). */
export const years = (y: number): number => y * SIM_SECONDS_PER_YEAR;

/** Real days → sim-seconds (life-history clock). */
export const days = (d: number): number => (d / 365) * SIM_SECONDS_PER_YEAR;

/** Real kilograms → dimensionless body scale. */
export const kg = (m: number): number => m / REF_MASS_KG;

// ---------------------------------------------------------------------------
// SPEED, EXPRESSED AS A REAL-TIME MULTIPLIER
// ---------------------------------------------------------------------------
// The sim advances `dt` sim-seconds per tick and runs `tps` ticks per second, so
// it covers `tps * dt` sim-seconds per real second. Because MOVEMENT is literal
// (above), that product IS the real-time multiplier: at 1x a deer crosses the
// ground at the speed a deer crosses ground.
//
// Be careful what you promise the user with it. Only motion is 1:1 — life
// history is compressed by SIM_SECONDS_PER_YEAR, so even at 1x a generation
// passes in minutes rather than years. "1x" therefore means "life-like motion",
// not "life-like lifespans", and the UI should say so rather than imply the sim
// is a documentary running in real time.

/** Real-time multiplier → target ticks per second. */
export const tpsForSpeed = (mult: number, dt: number): number => mult / dt;

/** Target ticks per second → real-time multiplier. */
export const speedForTps = (tps: number, dt: number): number => tps * dt;

/**
 * How long a mid-range deer's whole life takes at this multiplier, in real
 * seconds — the most legible answer to "what does this speed actually mean?".
 * Uses 8 years, the middle of the deer lifespan gene.
 */
export const lifetimeSecondsAt = (mult: number): number =>
  mult > 0 ? years(8) / mult : 0;
